import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config.js';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { format, parseISO, isValid as isValidDate, isWithinInterval, eachDayOfInterval } from 'date-fns';

const Reports = ({ currentUser }) => {
  const [activeReport, setActiveReport] = useState('users');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // User Audit Data
  const [users, setUsers] = useState([]);
  const [tenantStats, setTenantStats] = useState(null);
  
  // System Setup Data
  const [tenantConfigurations, setTenantConfigurations] = useState({});
  const [selectedConfigs, setSelectedConfigs] = useState({
    academic_departments: true,
    calendar_assignments: true,
    email_system: true,
    modules: true,
    periods: true,
    schedule_types: true,
    school_departments: true,
    school_holidays: true,
    substitute_registration: false,
    system_config: true,
    volunteer_custom_fields: false,
    volunteer_events: true,
    volunteer_registration: false
  });

  // Helper function to determine if a user is active
  const isUserActive = (user) => {
    if (user.hasOwnProperty('status') && typeof user.status === 'string') {
      return user.status.toLowerCase() === 'active';
    }
    return true;
  };

  // Activity Report Data
  const [absenceReport, setAbsenceReport] = useState(null);
  const [subReport, setSubReport] = useState(null);
  const [volunteerReport, setVolunteerReport] = useState(null);
  const [volunteerEvents, setVolunteerEvents] = useState([]);

  // Date filters
  const [activityStartDate, setActivityStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [activityEndDate, setActivityEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedUser, setSelectedUser] = useState('all');

  // Check if user has admin access
  const hasAdminAccess = currentUser?.isSystemAdmin || currentUser?.isEducationalAdmin || currentUser?.role === 'HR';

  // Fetch tenant configurations
  const fetchTenantConfigurations = useCallback(async () => {
    if (!currentUser?.tenantId) return;
    setIsLoading(true);
    setError(null);
    try {
      const configQuery = query(
        collection(db, 'tenantConfigurations'),
        where('tenantId', '==', currentUser.tenantId)
      );
      const configSnapshot = await getDocs(configQuery);
      const configs = {};
      configSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.configKey) {
          configs[data.configKey] = data.configValue || {};
        }
      });
      setTenantConfigurations(configs);
    } catch (err) {
      console.error('Error fetching tenant configurations:', err);
      setError('Failed to fetch tenant configurations: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  // Fetch all users
  const fetchUsers = useCallback(async () => {
    if (!currentUser?.tenantId) return;
    setIsLoading(true);
    setError(null);
    try {
      const usersQuery = query(
        collection(db, 'faculty_staff'),
        where('tenantId', '==', currentUser.tenantId),
        orderBy('name', 'asc')
      );
      const usersSnapshot = await getDocs(usersQuery);
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  // Fetch tenant stats using cloud function
  const fetchTenantStats = useCallback(async () => {
    if (!currentUser?.tenantId) return;
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://us-east1-reliefrosternew.cloudfunctions.net/gettenantstats?tenantId=${currentUser.tenantId}`
      );
      if (!response.ok) throw new Error('Failed to fetch tenant stats');
      const data = await response.json();
      setTenantStats(data);
    } catch (err) {
      console.error('Error fetching tenant stats:', err);
      setTenantStats(null);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  // Generate Absence Activity Report
  const generateAbsenceReport = async () => {
    if (!activityStartDate || !activityEndDate) {
      alert('Please select both start and end dates');
      return;
    }

    const start = parseISO(activityStartDate);
    const end = parseISO(activityEndDate);

    if (!isValidDate(start) || !isValidDate(end) || start > end) {
      alert('Invalid date range');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      let absenceQuery = query(
        collection(db, 'absence_requests'),
        where('tenantId', '==', currentUser.tenantId),
        where('status', '==', 'approved')
      );

      const absenceSnapshot = await getDocs(absenceQuery);
      const absences = [];

      absenceSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!data.startDate || !data.endDate) return;

        const absenceStart = parseISO(data.startDate);
        const absenceEnd = parseISO(data.endDate);

        if (!isValidDate(absenceStart) || !isValidDate(absenceEnd)) return;

        if (
          (absenceStart <= end && absenceEnd >= start) ||
          isWithinInterval(absenceStart, { start, end }) ||
          isWithinInterval(absenceEnd, { start, end })
        ) {
          if (selectedUser !== 'all' && data.requesterUid !== selectedUser) return;

          const user = users.find(u => u.uid === data.requesterUid);
          absences.push({
            id: doc.id,
            ...data,
            department: user?.department || 'N/A',
            displayReason: data.reason === 'Other' ? (data.reasonOther || 'Other') : data.reason
          });
        }
      });

      absences.sort((a, b) => parseISO(a.startDate) - parseISO(b.startDate));

      const totalDays = absences.reduce((sum, absence) => {
        const days = eachDayOfInterval({
          start: parseISO(absence.startDate),
          end: parseISO(absence.endDate)
        }).length;
        return sum + days;
      }, 0);

      const byDepartment = {};
      const byReason = {};
      const byUser = {};

      absences.forEach(absence => {
        const dept = absence.department;
        const reason = absence.displayReason;
        const userName = absence.requesterName;

        const days = eachDayOfInterval({
          start: parseISO(absence.startDate),
          end: parseISO(absence.endDate)
        }).length;

        byDepartment[dept] = (byDepartment[dept] || 0) + days;
        byReason[reason] = (byReason[reason] || 0) + days;
        byUser[userName] = (byUser[userName] || 0) + days;
      });

      setAbsenceReport({
        absences,
        statistics: {
          totalAbsences: absences.length,
          totalDays,
          byDepartment,
          byReason,
          byUser
        },
        startDate: activityStartDate,
        endDate: activityEndDate
      });
    } catch (err) {
      console.error('Error generating absence report:', err);
      setError('Failed to generate absence report: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate Subbing Activity Report
  const generateSubReport = async () => {
    if (!activityStartDate || !activityEndDate) {
      alert('Please select both start and end dates');
      return;
    }

    const start = parseISO(activityStartDate);
    const end = parseISO(activityEndDate);

    if (!isValidDate(start) || !isValidDate(end) || start > end) {
      alert('Invalid date range');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const subRequestsQuery = query(
        collection(db, 'sub_requests'),
        where('tenantId', '==', currentUser.tenantId)
      );

      const subSnapshot = await getDocs(subRequestsQuery);
      const subRequests = [];

      subSnapshot.docs.forEach(doc => {
        const data = doc.data();
        let requestDate;

        if (data.date?.toDate) {
          requestDate = data.date.toDate();
        } else if (typeof data.date === 'string') {
          requestDate = parseISO(data.date);
        }

        if (!requestDate || !isValidDate(requestDate)) return;

        if (isWithinInterval(requestDate, { start, end })) {
          if (selectedUser !== 'all' && data.requesterUid !== selectedUser) return;

          const periods = data.periods || [];
          periods.forEach(period => {
            const filled = data.filled?.[period];
            subRequests.push({
              id: doc.id,
              date: format(requestDate, 'yyyy-MM-dd'),
              displayDate: format(requestDate, 'EEEE, MMM d, yyyy'),
              teacher: data.name || 'Unknown',
              department: data.department || 'N/A',
              period,
              room: data.room || 'N/A',
              reason: data.reason || '',
              note: data.periodNotes?.[period] || '',
              filled: !!filled,
              substitute: filled?.name || null,
              filledAt: filled?.timestamp || null
            });
          });
        }
      });

      subRequests.sort((a, b) => a.date.localeCompare(b.date));

      const totalRequests = subRequests.length;
      const filledRequests = subRequests.filter(r => r.filled).length;
      const unfilledRequests = totalRequests - filledRequests;

      const byTeacher = {};
      const bySub = {};
      const byDepartment = {};

      subRequests.forEach(request => {
        byTeacher[request.teacher] = (byTeacher[request.teacher] || 0) + 1;
        byDepartment[request.department] = (byDepartment[request.department] || 0) + 1;
        
        if (request.filled && request.substitute) {
          bySub[request.substitute] = (bySub[request.substitute] || 0) + 1;
        }
      });

      setSubReport({
        requests: subRequests,
        statistics: {
          totalRequests,
          filledRequests,
          unfilledRequests,
          fillRate: totalRequests > 0 ? ((filledRequests / totalRequests) * 100).toFixed(1) : 0,
          byTeacher,
          bySub,
          byDepartment
        },
        startDate: activityStartDate,
        endDate: activityEndDate
      });
    } catch (err) {
      console.error('Error generating sub report:', err);
      setError('Failed to generate sub report: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch volunteer data
  const fetchVolunteerData = useCallback(async () => {
    if (!currentUser?.tenantId) return;
    setIsLoading(true);
    setError(null);
    try {
      // Fetch approved volunteers
      const volunteersQuery = query(
        collection(db, 'volunteers'),
        where('tenantId', '==', currentUser.tenantId),
        where('status', '==', 'active')
      );
      const volunteersSnapshot = await getDocs(volunteersQuery);
      const volunteers = volunteersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch volunteer events
      const eventsQuery = query(
        collection(db, 'volunteerEvents'),
        where('tenantId', '==', currentUser.tenantId),
        orderBy('startDate', 'asc')
      );
      const eventsSnapshot = await getDocs(eventsQuery);
      const events = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        signupCount: doc.data().signups ? Object.keys(doc.data().signups).length : 0
      }));

      setVolunteerEvents(events);
      setVolunteerReport({
        volunteers,
        events,
        statistics: {
          totalVolunteers: volunteers.length,
          totalEvents: events.length,
          totalSignups: events.reduce((sum, e) => sum + e.signupCount, 0)
        }
      });
    } catch (err) {
      console.error('Error fetching volunteer data:', err);
      setError('Failed to fetch volunteer data: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  // CSV Export Function
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          if (value === null || value === undefined) return '';
          const stringValue = String(value);
          return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')
            ? `"${stringValue.replace(/"/g, '""')}"`
            : stringValue;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  // Load data when active report changes
  useEffect(() => {
    if (!hasAdminAccess) return;

    switch (activeReport) {
      case 'users':
        fetchUsers();
        break;
      case 'absence':
        fetchUsers();
        break;
      case 'subbing':
        fetchUsers();
        break;
      case 'volunteer':
        fetchVolunteerData();
        break;
      case 'setup':
        fetchTenantStats();
        fetchTenantConfigurations();
        break;
      default:
        break;
    }
  }, [activeReport, hasAdminAccess, fetchUsers, fetchVolunteerData, fetchTenantStats, fetchTenantConfigurations]);

  // Print function with body class
  const handlePrint = () => {
    document.body.classList.add('printing');
    setTimeout(() => {
      window.print();
      document.body.classList.remove('printing');
    }, 100);
  };

  // Toggle config selection
  const toggleConfig = (configKey) => {
    setSelectedConfigs(prev => ({
      ...prev,
      [configKey]: !prev[configKey]
    }));
  };

  // Render configuration details
  const renderConfigDetails = (configKey, configData) => {
    if (!configData) return null;

    const configLabels = {
      academic_departments: 'Academic Departments',
      calendar_assignments: 'Calendar Assignments',
      email_system: 'Email System',
      modules: 'Enabled Modules',
      periods: 'School Periods',
      schedule_types: 'Schedule Types',
      school_departments: 'School Departments',
      school_holidays: 'School Holidays',
      substitute_registration: 'Substitute Registration Form',
      system_config: 'System Configuration',
      volunteer_custom_fields: 'Volunteer Custom Fields',
      volunteer_events: 'Volunteer Events Settings',
      volunteer_registration: 'Volunteer Registration Form'
    };

    return (
      <div className="report-section" key={configKey}>
        <h3>{configLabels[configKey] || configKey}</h3>
        {renderConfigContent(configKey, configData)}
      </div>
    );
  };

  // Render specific configuration content
  const renderConfigContent = (configKey, configData) => {
    switch (configKey) {
      case 'academic_departments':
      case 'school_departments':
        return (
          <div>
            {configData.departments && configData.departments.length > 0 && (
              <div>
                <h4>Departments ({configData.departments.length})</h4>
                <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
                  {configData.departments.map((dept, idx) => (
                    <li key={idx}>{dept}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'modules':
        return (
          <div>
            {configData.enabledModules && (
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Absence Management</td>
                    <td>{configData.enabledModules.absenceManagement ? '✓ Enabled' : '✗ Disabled'}</td>
                  </tr>
                  <tr>
                    <td>Substitute Management</td>
                    <td>{configData.enabledModules.substituteManagement ? '✓ Enabled' : '✗ Disabled'}</td>
                  </tr>
                  <tr>
                    <td>Sub Pay Management</td>
                    <td>{configData.enabledModules.subPayManagement ? '✓ Enabled' : '✗ Disabled'}</td>
                  </tr>
                  <tr>
                    <td>Volunteer Management</td>
                    <td>{configData.enabledModules.volunteerManagement ? '✓ Enabled' : '✗ Disabled'}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        );

      case 'periods':
        return (
          <div>
            {configData.periods && configData.periods.length > 0 && (
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                  </tr>
                </thead>
                <tbody>
                  {configData.periods.map((period, idx) => (
                    <tr key={idx}>
                      <td>{period.name}</td>
                      <td>{period.start_time}</td>
                      <td>{period.end_time}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );

      case 'schedule_types':
        return (
          <div>
            {configData.scheduleTypes && configData.scheduleTypes.length > 0 && (
              <div>
                {configData.scheduleTypes.map((scheduleType, idx) => (
                  <div key={idx} style={{ marginBottom: '25px' }}>
                    <h4>{scheduleType.name} {scheduleType.isDefault && '(Default)'}</h4>
                    <p style={{ marginBottom: '10px' }}><strong>Description:</strong> {scheduleType.description}</p>
                    {scheduleType.periods && scheduleType.periods.length > 0 && (
                      <table className="report-table">
                        <thead>
                          <tr>
                            <th>Period</th>
                            <th>Start Time</th>
                            <th>End Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scheduleType.periods.map((period, pIdx) => (
                            <tr key={pIdx}>
                              <td>{period.name}</td>
                              <td>{period.start_time}</td>
                              <td>{period.end_time}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'school_holidays':
        return (
          <div>
            {configData.holidays && configData.holidays.length > 0 && (
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Holiday Name</th>
                    <th>Date/Range</th>
                    <th>Type</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {configData.holidays.map((holiday, idx) => (
                    <tr key={idx}>
                      <td>{holiday.name}</td>
                      <td>
                        {holiday.type === 'single' 
                          ? holiday.date 
                          : `${holiday.startDate} to ${holiday.endDate}`}
                      </td>
                      <td style={{ textTransform: 'capitalize' }}>{holiday.type}</td>
                      <td>{holiday.enabled ? 'Enabled' : 'Disabled'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );

      case 'email_system':
        return (
          <div>
            <table className="report-table">
              <tbody>
                <tr>
                  <td><strong>Auto Send to Department</strong></td>
                  <td>{configData.autoSendDepartment ? 'Yes' : 'No'}</td>
                </tr>
                <tr>
                  <td><strong>Auto Send to Faculty</strong></td>
                  <td>{configData.autoSendFaculty ? 'Yes' : 'No'}</td>
                </tr>
                <tr>
                  <td><strong>Use Supervisor for Sub Requests</strong></td>
                  <td>{configData.useSupervisorForSubRequests ? 'Yes' : 'No'}</td>
                </tr>
                {configData.timing && (
                  <>
                    <tr>
                      <td><strong>Sub Availability Timing</strong></td>
                      <td>{configData.timing.subAvailability} at {configData.timing.subAvailabilityTime}</td>
                    </tr>
                    <tr>
                      <td><strong>Faculty Timing</strong></td>
                      <td>{configData.timing.facultyTiming?.day} at {configData.timing.facultyTiming?.time}</td>
                    </tr>
                    <tr>
                      <td><strong>Department Timing</strong></td>
                      <td>{configData.timing.departmentTiming?.day} at {configData.timing.departmentTiming?.time}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
            {configData.recipients && (
              <div style={{ marginTop: '20px' }}>
                <h4>Email Recipients</h4>
                <table className="report-table">
                  <tbody>
                    <tr>
                      <td><strong>Absence Requests</strong></td>
                      <td>{configData.recipients.absenceRequests}</td>
                    </tr>
                    <tr>
                      <td><strong>Sub Assignments</strong></td>
                      <td>{configData.recipients.subAssignments}</td>
                    </tr>
                    <tr>
                      <td><strong>Sub Availability</strong></td>
                      <td>{configData.recipients.subAvailability}</td>
                    </tr>
                    <tr>
                      <td><strong>Sub Requests</strong></td>
                      <td>{configData.recipients.subRequests}</td>
                    </tr>
                    <tr>
                      <td><strong>Volunteer Applications</strong></td>
                      <td>{configData.recipients.volunteerApplications}</td>
                    </tr>
                    <tr>
                      <td><strong>Volunteer Events</strong></td>
                      <td>{configData.recipients.volunteerEvents}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );

      case 'system_config':
        return (
          <table className="report-table">
            <tbody>
              <tr>
                <td><strong>Date Format</strong></td>
                <td>{configData.dateFormat || 'Not set'}</td>
              </tr>
              <tr>
                <td><strong>Time Format</strong></td>
                <td>{configData.timeFormat || 'Not set'}</td>
              </tr>
              <tr>
                <td><strong>Timezone</strong></td>
                <td>{configData.timezone || 'Not set'}</td>
              </tr>
            </tbody>
          </table>
        );

      case 'volunteer_events':
        return (
          <table className="report-table">
            <tbody>
              <tr>
                <td><strong>Allow Event Categories</strong></td>
                <td>{configData.allowEventCategories ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <td><strong>Allow Holiday Events</strong></td>
                <td>{configData.allowHolidayEvents ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <td><strong>Allow Multi-Day Events</strong></td>
                <td>{configData.allowMultiDayEvents ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <td><strong>Allow Weekend Events</strong></td>
                <td>{configData.allowWeekendEvents ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <td><strong>Default Event Duration</strong></td>
                <td>{configData.defaultEventDuration} hours</td>
              </tr>
              <tr>
                <td><strong>Max Advance Booking Days</strong></td>
                <td>{configData.maxAdvanceBookingDays}</td>
              </tr>
              <tr>
                <td><strong>Min Advance Notice Hours</strong></td>
                <td>{configData.minAdvanceNoticeHours}</td>
              </tr>
              <tr>
                <td><strong>Require Event Description</strong></td>
                <td>{configData.requireEventDescription ? 'Yes' : 'No'}</td>
              </tr>
            </tbody>
          </table>
        );

      case 'calendar_assignments':
        return (
          <div>
            {configData.patterns && configData.patterns.length > 0 && (
              <div>
                {configData.patterns.map((pattern, idx) => (
                  <div key={idx} style={{ marginBottom: '25px' }}>
                    <h4>Pattern {idx + 1}: {pattern.name || 'Unnamed'}</h4>
                    <p><strong>Type:</strong> {pattern.type}</p>
                    <p><strong>Rotation Days:</strong> {pattern.rotationDays}</p>
                    {pattern.startDate && <p><strong>Start Date:</strong> {pattern.startDate}</p>}
                    {pattern.scheduleMapping && (
                      <div>
                        <h5>Schedule Mapping</h5>
                        <table className="report-table">
                          <tbody>
                            {Object.entries(pattern.scheduleMapping).map(([day, schedule]) => (
                              <tr key={day}>
                                <td><strong>{day}</strong></td>
                                <td>{schedule}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'substitute_registration':
      case 'volunteer_registration':
        return (
          <div>
            {configData.sections && (
              <div>
                <h4>Form Sections</h4>
                {Object.entries(configData.sections).map(([sectionKey, section]) => (
                  <div key={sectionKey} style={{ marginBottom: '25px' }}>
                    <h5 style={{ textTransform: 'capitalize' }}>{section.title}</h5>
                    {section.description && <p style={{ fontStyle: 'italic', marginBottom: '10px' }}>{section.description}</p>}
                    <p><strong>Enabled:</strong> {section.enabled ? 'Yes' : 'No'}</p>
                    {section.fields && (
                      <table className="report-table">
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>Label</th>
                            <th>Type</th>
                            <th>Required</th>
                            <th>Enabled</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(section.fields).map(([fieldKey, field]) => (
                            <tr key={fieldKey}>
                              <td>{fieldKey}</td>
                              <td>{field.label}</td>
                              <td>{field.type}</td>
                              <td>{field.required ? 'Yes' : 'No'}</td>
                              <td>{field.enabled ? 'Yes' : 'No'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            )}
            {configData.settings && (
              <div style={{ marginTop: '25px' }}>
                <h4>Settings</h4>
                <table className="report-table">
                  <tbody>
                    {Object.entries(configData.settings).map(([key, value]) => (
                      <tr key={key}>
                        <td><strong>{key.replace(/([A-Z])/g, ' $1').trim()}</strong></td>
                        <td>{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );

      case 'volunteer_custom_fields':
        return (
          <div>
            {configData.fields && configData.fields.length > 0 && (
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Field Name</th>
                    <th>Type</th>
                    <th>Required</th>
                    <th>Preset</th>
                  </tr>
                </thead>
                <tbody>
                  {configData.fields.map((field, idx) => (
                    <tr key={idx}>
                      <td>{field.name}</td>
                      <td>{field.type}</td>
                      <td>{field.required ? 'Yes' : 'No'}</td>
                      <td>{field.isPreset ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );

      default:
        return <pre style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{JSON.stringify(configData, null, 2)}</pre>;
    }
  };

  // Don't render if no admin access
  if (!hasAdminAccess) {
    return (
      <div className="reports-container">
        <div className="error-message">
          <h2>Access Denied</h2>
          <p>You need administrator privileges to access reports.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-container">
      {/* Header */}
      <div className="reports-header no-print">
        <h1>📊 Reports & Analytics</h1>
        <p>Comprehensive reporting for your organization</p>
      </div>

      {/* Tab Navigation */}
      <div className="report-tabs no-print">
        <button 
          className={`tab-btn ${activeReport === 'users' ? 'active' : ''}`}
          onClick={() => setActiveReport('users')}
        >
          👥 User Audit
        </button>
        <button 
          className={`tab-btn ${activeReport === 'absence' ? 'active' : ''}`}
          onClick={() => setActiveReport('absence')}
        >
          📅 Absence Activity
        </button>
        <button 
          className={`tab-btn ${activeReport === 'subbing' ? 'active' : ''}`}
          onClick={() => setActiveReport('subbing')}
        >
          📝 Subbing Activity
        </button>
        <button 
          className={`tab-btn ${activeReport === 'volunteer' ? 'active' : ''}`}
          onClick={() => setActiveReport('volunteer')}
        >
          🤝 Volunteer Report
        </button>
        <button 
          className={`tab-btn ${activeReport === 'setup' ? 'active' : ''}`}
          onClick={() => setActiveReport('setup')}
        >
          ⚙️ System Setup
        </button>
      </div>

      {/* Loading & Error States */}
      {isLoading && (
        <div className="loading-message">
          <p>Loading data...</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {/* Report Content */}
      <div className="report-content">
        {/* USER AUDIT REPORT */}
        {activeReport === 'users' && (
          <>
            <div className="controls-row no-print">
              <button 
                onClick={handlePrint}
                className="action-btn print-btn"
              >
                🖨️ Print Report
              </button>
              <button 
                onClick={() => exportToCSV(
                  users.map(u => ({
                    'Name': u.name || 'N/A',
                    'Email': u.email || 'N/A',
                    'Role': u.role || 'N/A',
                    'Department': u.department || 'N/A',
                    'Status': isUserActive(u) ? 'Active' : 'Inactive',
                    'Is Admin': u.isEducationalAdmin || u.isSystemAdmin ? 'Yes' : 'No',
                    'Created At': u.createdAt ? format(u.createdAt.toDate(), 'yyyy-MM-dd') : 'N/A'
                  })),
                  'user_audit'
                )}
                className="action-btn export-btn"
              >
                📥 Export CSV
              </button>
            </div>

            <div className="report-header">
              <h2>User Audit Report</h2>
              <p className="report-meta">Generated on {format(new Date(), 'MMMM d, yyyy h:mm a')}</p>
              <p className="report-meta">Total Users: <strong>{users.length}</strong></p>
            </div>

            <div className="report-section">
              <h3>User Summary</h3>
              <div className="summary-stats">
                <div className="stat-box">
                  <div className="stat-label">Total Users</div>
                  <div className="stat-value">{users.length}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Active Users</div>
                  <div className="stat-value">{users.filter(u => isUserActive(u)).length}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Administrators</div>
                  <div className="stat-value">
                    {users.filter(u => u.isEducationalAdmin || u.isSystemAdmin).length}
                  </div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Faculty/Staff</div>
                  <div className="stat-value">
                    {users.filter(u => u.role === 'Faculty/Staff').length}
                  </div>
                </div>
              </div>
            </div>

            <div className="report-section">
              <h3>User List</h3>
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Admin</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>{user.name || 'N/A'}</td>
                      <td>{user.email || 'N/A'}</td>
                      <td>{user.role || 'N/A'}</td>
                      <td>{user.department || 'N/A'}</td>
                      <td>
                        <span className={`status-indicator ${isUserActive(user) ? 'active' : 'inactive'}`}>
                          {isUserActive(user) ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{user.isEducationalAdmin || user.isSystemAdmin ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ABSENCE ACTIVITY REPORT */}
        {activeReport === 'absence' && (
          <>
            <div className="controls-row no-print">
              <input
                type="date"
                value={activityStartDate}
                onChange={(e) => setActivityStartDate(e.target.value)}
                className="control-input"
              />
              <input
                type="date"
                value={activityEndDate}
                onChange={(e) => setActivityEndDate(e.target.value)}
                className="control-input"
              />
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="control-input"
              >
                <option value="all">All Users</option>
                {users.map(user => (
                  <option key={user.id} value={user.uid}>
                    {user.name}
                  </option>
                ))}
              </select>
              <button onClick={generateAbsenceReport} className="generate-btn">
                Generate Report
              </button>
            </div>

            {absenceReport && (
              <>
                <div className="controls-row no-print" style={{ marginTop: '15px' }}>
                  <button onClick={handlePrint} className="action-btn print-btn">
                    🖨️ Print Report
                  </button>
                  <button 
                    onClick={() => exportToCSV(
                      absenceReport.absences.map(a => ({
                        'Date': a.startDate,
                        'End Date': a.endDate,
                        'Name': a.requesterName,
                        'Department': a.department,
                        'Reason': a.displayReason,
                        'Notes': a.notes || ''
                      })),
                      'absence_activity'
                    )}
                    className="action-btn export-btn"
                  >
                    📥 Export CSV
                  </button>
                </div>

                <div className="report-header">
                  <h2>Absence Activity Report</h2>
                  <p className="report-meta">
                    Date Range: {format(parseISO(absenceReport.startDate), 'MMM d, yyyy')} - {format(parseISO(absenceReport.endDate), 'MMM d, yyyy')}
                  </p>
                  <p className="report-meta">Generated on {format(new Date(), 'MMMM d, yyyy h:mm a')}</p>
                </div>

                <div className="report-section">
                  <h3>Summary Statistics</h3>
                  <div className="summary-stats">
                    <div className="stat-box">
                      <div className="stat-label">Total Absences</div>
                      <div className="stat-value">{absenceReport.statistics.totalAbsences}</div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-label">Total Days</div>
                      <div className="stat-value">{absenceReport.statistics.totalDays}</div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-label">Avg Days/Absence</div>
                      <div className="stat-value">
                        {absenceReport.statistics.totalAbsences > 0 
                          ? (absenceReport.statistics.totalDays / absenceReport.statistics.totalAbsences).toFixed(1)
                          : 0}
                      </div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-label">Departments</div>
                      <div className="stat-value">
                        {Object.keys(absenceReport.statistics.byDepartment).length}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="report-section">
                  <h3>Absence Details</h3>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Name</th>
                        <th>Department</th>
                        <th>Reason</th>
                        <th>Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {absenceReport.absences.map(absence => {
                        const days = eachDayOfInterval({
                          start: parseISO(absence.startDate),
                          end: parseISO(absence.endDate)
                        }).length;
                        return (
                          <tr key={absence.id}>
                            <td>{format(parseISO(absence.startDate), 'MMM d, yyyy')}</td>
                            <td>{format(parseISO(absence.endDate), 'MMM d, yyyy')}</td>
                            <td>{absence.requesterName}</td>
                            <td>{absence.department}</td>
                            <td>{absence.displayReason}</td>
                            <td>{days}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="report-section">
                  <h3>By Department</h3>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Department</th>
                        <th>Total Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(absenceReport.statistics.byDepartment)
                        .sort((a, b) => b[1] - a[1])
                        .map(([dept, days]) => (
                          <tr key={dept}>
                            <td>{dept}</td>
                            <td>{days}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <div className="report-section">
                  <h3>By Reason</h3>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Reason</th>
                        <th>Total Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(absenceReport.statistics.byReason)
                        .sort((a, b) => b[1] - a[1])
                        .map(([reason, days]) => (
                          <tr key={reason}>
                            <td>{reason}</td>
                            <td>{days}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}

        {/* SUBBING ACTIVITY REPORT */}
        {activeReport === 'subbing' && (
          <>
            <div className="controls-row no-print">
              <input
                type="date"
                value={activityStartDate}
                onChange={(e) => setActivityStartDate(e.target.value)}
                className="control-input"
              />
              <input
                type="date"
                value={activityEndDate}
                onChange={(e) => setActivityEndDate(e.target.value)}
                className="control-input"
              />
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="control-input"
              >
                <option value="all">All Users</option>
                {users.map(user => (
                  <option key={user.id} value={user.uid}>
                    {user.name}
                  </option>
                ))}
              </select>
              <button onClick={generateSubReport} className="generate-btn">
                Generate Report
              </button>
            </div>

            {subReport && (
              <>
                <div className="controls-row no-print" style={{ marginTop: '15px' }}>
                  <button onClick={handlePrint} className="action-btn print-btn">
                    🖨️ Print Report
                  </button>
                  <button 
                    onClick={() => exportToCSV(
                      subReport.requests.map(r => ({
                        'Date': r.displayDate,
                        'Teacher': r.teacher,
                        'Department': r.department,
                        'Period': r.period,
                        'Room': r.room,
                        'Status': r.filled ? 'Filled' : 'Unfilled',
                        'Substitute': r.substitute || 'N/A'
                      })),
                      'subbing_activity'
                    )}
                    className="action-btn export-btn"
                  >
                    📥 Export CSV
                  </button>
                </div>

                <div className="report-header">
                  <h2>Subbing Activity Report</h2>
                  <p className="report-meta">
                    Date Range: {format(parseISO(subReport.startDate), 'MMM d, yyyy')} - {format(parseISO(subReport.endDate), 'MMM d, yyyy')}
                  </p>
                  <p className="report-meta">Generated on {format(new Date(), 'MMMM d, yyyy h:mm a')}</p>
                </div>

                <div className="report-section">
                  <h3>Summary Statistics</h3>
                  <div className="summary-stats">
                    <div className="stat-box">
                      <div className="stat-label">Total Requests</div>
                      <div className="stat-value">{subReport.statistics.totalRequests}</div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-label">Filled</div>
                      <div className="stat-value">{subReport.statistics.filledRequests}</div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-label">Unfilled</div>
                      <div className="stat-value">{subReport.statistics.unfilledRequests}</div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-label">Fill Rate</div>
                      <div className="stat-value">{subReport.statistics.fillRate}%</div>
                    </div>
                  </div>
                </div>

                <div className="report-section">
                  <h3>Request Details</h3>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Teacher</th>
                        <th>Department</th>
                        <th>Period</th>
                        <th>Room</th>
                        <th>Status</th>
                        <th>Substitute</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subReport.requests.map((request, idx) => (
                        <tr key={idx}>
                          <td>{request.displayDate}</td>
                          <td>{request.teacher}</td>
                          <td>{request.department}</td>
                          <td>{request.period}</td>
                          <td>{request.room}</td>
                          <td>
                            <span className={`status-indicator ${request.filled ? 'filled' : 'unfilled'}`}>
                              {request.filled ? 'Filled' : 'Unfilled'}
                            </span>
                          </td>
                          <td>{request.substitute || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="report-section">
                  <h3>Top Requesters</h3>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Teacher</th>
                        <th>Requests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(subReport.statistics.byTeacher)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 10)
                        .map(([teacher, count]) => (
                          <tr key={teacher}>
                            <td>{teacher}</td>
                            <td>{count}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <div className="report-section">
                  <h3>Top Substitutes</h3>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Substitute</th>
                        <th>Jobs Filled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(subReport.statistics.bySub)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 10)
                        .map(([sub, count]) => (
                          <tr key={sub}>
                            <td>{sub}</td>
                            <td>{count}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}

        {/* VOLUNTEER REPORT */}
        {activeReport === 'volunteer' && volunteerReport && (
          <>
            <div className="controls-row no-print">
              <button onClick={handlePrint} className="action-btn print-btn">
                🖨️ Print Report
              </button>
              <button 
                onClick={() => exportToCSV(
                  volunteerReport.volunteers.map(v => ({
                    'Name': v.name || 'N/A',
                    'Email': v.email || 'N/A',
                    'Phone': v.phone || 'N/A',
                    'Status': v.status || 'N/A',
                    'Registered': v.createdAt ? format(v.createdAt.toDate(), 'yyyy-MM-dd') : 'N/A'
                  })),
                  'volunteer_list'
                )}
                className="action-btn export-btn"
              >
                📥 Export Volunteers
              </button>
              <button 
                onClick={() => exportToCSV(
                  volunteerReport.events.map(e => ({
                    'Title': e.title || 'N/A',
                    'Date': e.startDate || 'N/A',
                    'Location': e.location || 'N/A',
                    'Signups': e.signupCount,
                    'Max Volunteers': e.maxVolunteers || 'N/A'
                  })),
                  'volunteer_events'
                )}
                className="action-btn export-btn"
              >
                📥 Export Events
              </button>
            </div>

            <div className="report-header">
              <h2>Volunteer Report</h2>
              <p className="report-meta">Generated on {format(new Date(), 'MMMM d, yyyy h:mm a')}</p>
            </div>

            <div className="report-section">
              <h3>Summary Statistics</h3>
              <div className="summary-stats">
                <div className="stat-box">
                  <div className="stat-label">Total Volunteers</div>
                  <div className="stat-value">{volunteerReport.statistics.totalVolunteers}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Total Events</div>
                  <div className="stat-value">{volunteerReport.statistics.totalEvents}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Total Signups</div>
                  <div className="stat-value">{volunteerReport.statistics.totalSignups}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Avg Signups/Event</div>
                  <div className="stat-value">
                    {volunteerReport.statistics.totalEvents > 0 
                      ? (volunteerReport.statistics.totalSignups / volunteerReport.statistics.totalEvents).toFixed(1)
                      : 0}
                  </div>
                </div>
              </div>
            </div>

            <div className="report-section">
              <h3>Volunteer List</h3>
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {volunteerReport.volunteers.map(volunteer => (
                    <tr key={volunteer.id}>
                      <td>{volunteer.name || 'N/A'}</td>
                      <td>{volunteer.email || 'N/A'}</td>
                      <td>{volunteer.phone || 'N/A'}</td>
                      <td>
                        <span className="status-indicator active">
                          {volunteer.status || 'Approved'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="report-section">
              <h3>Volunteer Events</h3>
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Date</th>
                    <th>Location</th>
                    <th>Signups</th>
                    <th>Max</th>
                  </tr>
                </thead>
                <tbody>
                  {volunteerReport.events.map(event => (
                    <tr key={event.id}>
                      <td>{event.title || 'N/A'}</td>
                      <td>{event.startDate || 'N/A'}</td>
                      <td>{event.location || 'N/A'}</td>
                      <td>{event.signupCount}</td>
                      <td>{event.maxVolunteers || 'Unlimited'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* SYSTEM SETUP REPORT */}
        {activeReport === 'setup' && (
          <>
            <div className="controls-row no-print">
              <button onClick={handlePrint} className="action-btn print-btn">
                🖨️ Print Report
              </button>
            </div>

            {/* Configuration Selection */}
            <div className="report-section no-print">
              <h3>Select Configurations to Include</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px', marginTop: '15px' }}>
                {Object.keys(selectedConfigs).map(configKey => (
                  <label key={configKey} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px', background: '#f8f9fa', borderRadius: '6px', border: selectedConfigs[configKey] ? '2px solid #667eea' : '2px solid transparent' }}>
                    <input
                      type="checkbox"
                      checked={selectedConfigs[configKey]}
                      onChange={() => toggleConfig(configKey)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span style={{ fontSize: '0.95rem' }}>{configKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="report-header">
              <h2>System Setup Report</h2>
              <p className="report-meta">Generated on {format(new Date(), 'MMMM d, yyyy h:mm a')}</p>
            </div>

            <div className="report-section">
              <h3>Tenant Information</h3>
              <table className="report-table">
                <tbody>
                  <tr>
                    <td style={{ width: '30%' }}><strong>Tenant ID:</strong></td>
                    <td>{currentUser?.tenantId || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>School Name:</strong></td>
                    <td>{currentUser?.tenantName || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Domain:</strong></td>
                    <td>{currentUser?.domain || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {tenantStats && (
              <div className="report-section">
                <h3>Usage Statistics</h3>
                <div className="summary-stats">
                  <div className="stat-box">
                    <div className="stat-label">Total Users</div>
                    <div className="stat-value">{tenantStats.totalUsers || 0}</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">Active Users</div>
                    <div className="stat-value">{tenantStats.activeUsers || 0}</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">Departments</div>
                    <div className="stat-value">{tenantStats.totalDepartments || 0}</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">Roles</div>
                    <div className="stat-value">{tenantStats.totalRoles || 0}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Render selected configurations */}
            {Object.entries(selectedConfigs)
              .filter(([key, isSelected]) => isSelected && tenantConfigurations[key])
              .map(([configKey]) => renderConfigDetails(configKey, tenantConfigurations[configKey]))}

            {Object.values(selectedConfigs).every(v => !v) && (
              <div className="report-section">
                <p style={{ textAlign: 'center', color: '#666', padding: '40px', background: '#f8f9fa', borderRadius: '8px' }}>
                  Please select at least one configuration to display in the report.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        .reports-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px;
          background: #f5f7fa;
          min-height: 100vh;
        }

        .reports-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 30px;
          border-radius: 12px;
          margin-bottom: 30px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }

        .reports-header h1 {
          margin: 0 0 10px 0;
          font-size: 2.5rem;
        }

        .reports-header p {
          margin: 0;
          opacity: 0.9;
          font-size: 1.1rem;
        }

        .report-tabs {
          display: flex;
          gap: 10px;
          margin-bottom: 30px;
          flex-wrap: wrap;
        }

        .tab-btn {
          padding: 12px 24px;
          border: none;
          background: white;
          color: #333;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 500;
          transition: all 0.3s ease;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .tab-btn:hover {
          background: #667eea;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }

        .tab-btn.active {
          background: #667eea;
          color: white;
          box-shadow: 0 4px 8px rgba(102,126,234,0.3);
        }

        .report-content {
          background: white;
          padding: 30px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .report-header {
          border-bottom: 3px solid #667eea;
          padding-bottom: 20px;
          margin-bottom: 30px;
        }

        .report-header h2 {
          margin: 0 0 10px 0;
          color: #2d3748;
          font-size: 2rem;
        }

        .report-meta {
          margin: 5px 0;
          color: #718096;
          font-size: 0.95rem;
        }

        .report-section {
          margin-bottom: 40px;
        }

        .report-section h3 {
          color: #2d3748;
          font-size: 1.4rem;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #e2e8f0;
        }

        .report-section h4 {
          color: #4a5568;
          font-size: 1.1rem;
          margin: 20px 0 10px 0;
        }

        .report-section h5 {
          color: #4a5568;
          font-size: 1rem;
          margin: 15px 0 10px 0;
        }

        .summary-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-box {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 25px;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          transition: transform 0.2s ease;
        }

        .stat-box:hover {
          transform: translateY(-5px);
          box-shadow: 0 6px 12px rgba(0,0,0,0.15);
        }

        .stat-label {
          font-size: 0.9rem;
          opacity: 0.9;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-value {
          font-size: 2.5rem;
          font-weight: bold;
          line-height: 1;
        }

        .report-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 15px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .report-table thead {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .report-table th {
          padding: 15px;
          text-align: left;
          font-weight: 600;
          text-transform: uppercase;
          font-size: 0.85rem;
          letter-spacing: 0.5px;
        }

        .report-table td {
          padding: 12px 15px;
          border-bottom: 1px solid #e2e8f0;
          color: #4a5568;
        }

        .report-table tbody tr:hover {
          background-color: #f7fafc;
        }

        .report-table tbody tr:last-child td {
          border-bottom: none;
        }

        .status-indicator {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .status-indicator.active {
          background-color: #c6f6d5;
          color: #22543d;
        }

        .status-indicator.inactive {
          background-color: #fed7d7;
          color: #742a2a;
        }

        .status-indicator.filled {
          background-color: #c6f6d5;
          color: #22543d;
        }

        .status-indicator.unfilled {
          background-color: #feebc8;
          color: #7c2d12;
        }

        .controls-row {
          display: flex;
          gap: 15px;
          margin-bottom: 25px;
          flex-wrap: wrap;
          align-items: center;
        }

        .control-input {
          padding: 10px 15px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 1rem;
          min-width: 200px;
          transition: border-color 0.3s ease;
        }

        .control-input:focus {
          outline: none;
          border-color: #667eea;
        }

        .generate-btn {
          padding: 10px 25px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .generate-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102,126,234,0.4);
        }

        .action-btn {
          padding: 10px 20px;
          border: none;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .print-btn {
          background: #48bb78;
          color: white;
        }

        .print-btn:hover {
          background: #38a169;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(72,187,120,0.4);
        }

        .export-btn {
          background: #4299e1;
          color: white;
        }

        .export-btn:hover {
          background: #3182ce;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(66,153,225,0.4);
        }

        .loading-message, .error-message {
          text-align: center;
          padding: 60px 20px;
          color: #718096;
        }

        .error-message {
          color: #e53e3e;
          background: #fff5f5;
          border: 2px solid #fc8181;
          border-radius: 8px;
        }

        /* Print Styles */
        @media print {
          @page {
            size: letter;
            margin: 0.5in;
          }

          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Hide sidebar and navigation */
          .sidebar,
          div[class*="sidebar"],
          aside,
          nav,
          header[class*="nav"],
          div[class*="nav"] {
            display: none !important;
            visibility: hidden !important;
            width: 0 !important;
            height: 0 !important;
          }

          /* Reset layout for main content */
          body > div,
          body > div > div,
          body > div > div > div {
            margin-left: 0 !important;
            margin-right: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
          }

          .reports-container {
            padding: 0 !important;
            background: white !important;
            max-width: 100% !important;
            margin: 0 !important;
            width: 100% !important;
          }

          .no-print {
            display: none !important;
          }

          .report-content {
            box-shadow: none !important;
            padding: 0 !important;
            background: white !important;
            margin: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
          }

          .report-header {
            border-bottom: 3px solid #000;
            padding-bottom: 12px;
            margin-bottom: 20px;
            width: 100% !important;
          }

          .report-header h2 {
            font-size: 22pt;
            color: #000;
            margin: 0 0 8px 0;
            font-weight: bold;
          }

          .report-meta {
            font-size: 9.5pt;
            color: #333;
            margin: 3px 0;
          }

          .report-section {
            page-break-inside: avoid;
            margin-bottom: 20px;
            width: 100% !important;
            max-width: 100% !important;
          }

          .report-section h3 {
            font-size: 15pt;
            color: #000;
            border-bottom: 2px solid #666;
            padding-bottom: 5px;
            margin: 20px 0 12px 0;
            page-break-after: avoid;
            width: 100% !important;
            font-weight: bold;
          }

          .report-section h4 {
            font-size: 12pt;
            color: #333;
            margin: 15px 0 8px 0;
            page-break-after: avoid;
            font-weight: bold;
          }

          .report-section h5 {
            font-size: 11pt;
            color: #444;
            margin: 12px 0 6px 0;
            page-break-after: avoid;
            font-weight: 600;
          }

          .report-section ul {
            margin: 8px 0 15px 0;
            padding-left: 25px;
            line-height: 1.6;
          }

          .report-section ul li {
            line-height: 1.7;
            margin-bottom: 3px;
            page-break-inside: avoid;
          }

          .report-section p {
            margin: 8px 0;
            line-height: 1.5;
          }

          .summary-stats {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 10px;
            margin-bottom: 18px;
            page-break-inside: avoid;
            width: 100% !important;
          }

          .stat-box {
            background: #f8f8f8 !important;
            color: #000 !important;
            padding: 10px 12px;
            border: 1.5px solid #ccc;
            border-radius: 3px;
          }

          .stat-label {
            font-size: 8.5pt;
            color: #555 !important;
            margin-bottom: 4px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }

          .stat-value {
            font-size: 18pt;
            color: #000 !important;
            font-weight: bold;
            line-height: 1;
          }

          .report-table {
            width: 100% !important;
            max-width: 100% !important;
            border-collapse: collapse;
            page-break-inside: auto;
            font-size: 9.5pt;
            margin: 0 !important;
          }

          .report-table thead {
            background: #e8e8e8 !important;
          }

          .report-table th {
            border: 1px solid #999;
            padding: 10px 8px;
            text-align: left;
            font-weight: bold;
            color: #000 !important;
            background: #e8e8e8 !important;
            font-size: 10pt;
          }

          .report-table td {
            border: 1px solid #ccc;
            padding: 8px 6px;
            color: #000;
            vertical-align: top;
            line-height: 1.4;
          }

          .report-table tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          .report-table tbody tr:nth-child(even) {
            background: #fafafa !important;
          }

          .status-indicator {
            padding: 3px 8px;
            border-radius: 3px;
            font-size: 8.5pt;
            font-weight: bold;
            display: inline-block;
          }

          .status-indicator.active {
            background: #d4edda !important;
            color: #155724 !important;
            border: 1px solid #c3e6cb !important;
          }

          .status-indicator.inactive {
            background: #f8d7da !important;
            color: #721c24 !important;
            border: 1px solid #f5c6cb !important;
          }

          .status-indicator.filled {
            background: #d4edda !important;
            color: #155724 !important;
            border: 1px solid #c3e6cb !important;
          }

          .status-indicator.unfilled {
            background: #fff3cd !important;
            color: #856404 !important;
            border: 1px solid #ffeaa7 !important;
          }

          pre {
            font-size: 8pt;
            white-space: pre-wrap;
            word-wrap: break-word;
            page-break-inside: avoid;
            width: 100% !important;
            overflow-x: hidden;
            background: #f8f8f8 !important;
            border: 1px solid #ddd !important;
            padding: 8px !important;
            border-radius: 3px;
            line-height: 1.4;
          }
        }

        body.printing {
          overflow-x: hidden;
        }

        body.printing .sidebar,
        body.printing div[class*="sidebar"],
        body.printing aside,
        body.printing nav {
          display: none !important;
        }

        body.printing > div,
        body.printing > div > div {
          margin-left: 0 !important;
          margin-right: 0 !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .reports-container {
            padding: 15px;
          }

          .reports-header {
            padding: 20px;
          }

          .reports-header h1 {
            font-size: 2rem;
          }

          .report-tabs {
            flex-direction: column;
          }

          .tab-btn {
            width: 100%;
          }

          .report-content {
            padding: 20px;
          }

          .controls-row {
            flex-direction: column;
            align-items: stretch;
          }

          .control-input {
            width: 100%;
            min-width: auto;
          }

          .generate-btn {
            width: 100%;
          }

          .summary-stats {
            grid-template-columns: 1fr;
          }

          .report-table {
            font-size: 0.85rem;
          }

          .report-table th,
          .report-table td {
            padding: 8px 10px;
          }

          .action-btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default Reports;